import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, SquarePen, ClipboardCheck, CopyPlus } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { TemplateGalleryDialog } from './Templates/TemplateGalleryDialog';

interface CreateFormMenuOptionProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  disabled?: boolean;
  onSelect?: () => void;
  testId: string;
}

function CreateFormMenuOption({
  icon: Icon,
  title,
  description,
  disabled,
  onSelect,
  testId,
}: CreateFormMenuOptionProps) {
  return (
    <DropdownMenuItem
      onClick={onSelect}
      disabled={disabled}
      className="flex items-start gap-3 py-2.5"
      data-testid={testId}
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-sm font-medium leading-none">{title}</p>
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      </div>
    </DropdownMenuItem>
  );
}

export function CreateFormMenu() {
  const { t } = useTranslation('forms');
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const [galleryOpen, setGalleryOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button data-testid="forms-create-button">
            {t('list.createButton')}
            <ChevronDown className="h-4 w-4 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <CreateFormMenuOption
            icon={SquarePen}
            title={t('list.createMenu.blank.title')}
            description={t('list.createMenu.blank.description')}
            onSelect={() => navigate(`/${orgId}/forms/new`)}
            testId="forms-create-menu-blank"
          />
          <CreateFormMenuOption
            icon={ClipboardCheck}
            title={t('list.createMenu.template.title')}
            description={t('list.createMenu.template.description')}
            onSelect={() => setGalleryOpen(true)}
            testId="forms-create-menu-template"
          />
          <CreateFormMenuOption
            icon={CopyPlus}
            title={t('list.createMenu.newTemplate.title')}
            description={t('list.createMenu.newTemplate.description')}
            onSelect={() => navigate(`/${orgId}/forms/templates/new`)}
            testId="forms-create-menu-new-template"
          />
        </DropdownMenuContent>
      </DropdownMenu>

      <TemplateGalleryDialog open={galleryOpen} onOpenChange={setGalleryOpen} />
    </>
  );
}
